import { createTheme } from '@mui/material/styles'
import { createPalette, BRAND_PRIMARY } from './palette'
import { typography } from './typography'

const createAppTheme = (mode = 'light') => {
  const palette = createPalette(mode);
  const isDark = mode === 'dark';

  return createTheme({
    palette,
    typography,
    shape: {
      borderRadius: 8,
    },
    spacing: 8,
    components: {
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: isDark 
              ? '0px 1px 3px rgba(0, 0, 0, 0.3)' 
              : '0px 1px 3px rgba(0, 0, 0, 0.12)',
            backgroundColor: BRAND_PRIMARY, // Always use primary brand color regardless of theme mode
            color: '#ffffff', // Ensure text is always white on primary background
            // Prevent MUI dark mode overlay from tinting the AppBar
            '--Paper-overlay': 'none !important',
          },
        },
      },
      MuiButton: {
        defaultProps: {
          variant: 'contained',
          color: 'primary',
          size: 'medium',
        },
        styleOverrides: {
          root: {
            borderRadius: 8,
            padding: '8px 16px',
            textTransform: 'none',
          },
          sizeSmall: {
            height: 26,
          },
          sizeMedium: {
            height: 32,
          },
          sizeLarge: {
            height: 38,
          },
          contained: {
            boxShadow: 'none',

            '&:hover': {
              boxShadow: isDark 
                ? '0px 2px 4px rgba(0, 0, 0, 0.3)' 
                : '0px 2px 4px rgba(0, 0, 0, 0.1)',
            },
          },
          containedPrimary: {
            backgroundColor: palette.primary.light,
            color: 'white',

            '&:hover': {
              backgroundColor: palette.primary[500],
            },
          },
          containedSecondary: {
            backgroundColor: palette.secondary.light,
            color: 'white',

            '&:hover': {
              backgroundColor: palette.secondary[500],
            },
          },
          outlinedPrimary: {
            transition: 'color 0.15s, background-color 0.15s',
            borderColor: isDark ? palette.primary.light : palette.primary.main,
            color: isDark ? palette.primary.light : palette.primary.main,

            '&:hover': {
              borderColor: isDark ? palette.primary[400] : palette.primary.dark,
              color: isDark ? palette.primary[400] : palette.primary.dark,
            },
          },
          textPrimary: {
            transition: 'color 0.15s, background-color 0.15s',
            color: isDark ? palette.primary.light : palette.primary.main,

            '&:hover': {
              color: isDark ? palette.primary[400] : palette.primary.dark,
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: isDark 
              ? '0px 2px 8px rgba(0, 0, 0, 0.3)' 
              : '0px 2px 8px rgba(0, 0, 0, 0.08)',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRight: isDark 
              ? '1px solid rgba(255, 255, 255, 0.12)' 
              : '1px solid rgba(0, 0, 0, 0.08)',
          },
        },
      },
      MuiFab: {
        styleOverrides: {
          sizeSmall: {
            width: 32,
            height: 32,
            minHeight: 32,
          }
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            '&.Mui-focused': {
              color: isDark ? palette.primary[400] : palette.primary.main,
            },
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            margin: '4px 8px',
            '&.Mui-selected': {
              backgroundColor: isDark 
                ? `${BRAND_PRIMARY}40` 
                : '#37593414',
              '&:hover': {
                backgroundColor: isDark 
                  ? `${BRAND_PRIMARY}50` 
                  : '#3759341f',
              },
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '&.Mui-focused:not(.Mui-disabled):not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
              borderColor: isDark ? palette.primary.light : palette.primary.main,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: isDark ? palette.primary.light : palette.primary.main,
            },
          },
        },
      },
      MuiPickersOutlinedInput: {
        styleOverrides: {
          root: {
            '&.Mui-focused:not(.Mui-disabled):not(.Mui-error) .MuiPickersOutlinedInput-notchedOutline': {
              borderColor: isDark ? palette.primary.light : palette.primary.main,
            },
            '&.Mui-focused .MuiPickersOutlinedInput-notchedOutline': {
              borderColor: isDark ? palette.primary.light : palette.primary.main,
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 8,
            },
          },
        },
      },
      MuiSnackbarContent: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontWeight: 500,
            '&.notistack-MuiContent-success': {
              backgroundColor: palette.success.main,
              color: '#ffffff',
            },
            '&.notistack-MuiContent-error': {
              backgroundColor: palette.error.main,
              color: '#ffffff',
            },
            '&.notistack-MuiContent-warning': {
              backgroundColor: palette.warning.main,
              color: '#ffffff',
            },
            '&.notistack-MuiContent-info': {
              backgroundColor: palette.info.main,
              color: '#ffffff',
            },
          },
        },
      },
    },
  });
};

// Default export for backward compatibility
const theme = createAppTheme('light');

export default theme;
export { createAppTheme };
